import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { FileBarChart, Download, TrendingUp, Users, DollarSign, Target } from "lucide-react";

export default function Reports() {
  const [summary, setSummary] = useState(null);
  useEffect(() => { api.get("/reports/summary").then(r => setSummary(r.data)); }, []);

  const [meta, setMeta] = useState(null);
  const print = async () => {
    try {
      const { data } = await api.post("/reports/serial", { type: "business" });
      setMeta(data);
      setTimeout(() => window.print(), 150);
    } catch { window.print(); }
  };

  return (
    <div className="space-y-6" data-testid="reports-page">
      {meta && (
        <div className="text-xs font-mono text-slate-600 dark:text-slate-300" data-testid="report-meta">
          Report No: {meta.serial_no} · Generated on: {meta.generated_at}
        </div>
      )}
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-amber-600 dark:text-amber-400">Analytics</div>
          <h1 className="font-display text-3xl md:text-4xl font-light tracking-tight mt-1">Reports</h1>
        </div>
        <Button onClick={print} className="bg-[#1E3A8A] btn-pill" data-testid="print-report"><Download className="w-4 h-4 mr-1.5" /> Print / PDF</Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Metric icon={DollarSign} label="Total Revenue" value={`₹${(summary?.total_revenue ?? 0).toLocaleString()}`} color="text-emerald-600" />
        <Metric icon={FileBarChart} label="Total Invoices" value={summary?.total_invoices ?? 0} color="text-blue-600" />
        <Metric icon={TrendingUp} label="Total GST" value={`₹${(summary?.total_gst ?? 0).toLocaleString()}`} color="text-amber-600" />
        <Metric icon={Target} label="Lead Stages" value={Object.keys(summary?.lead_conversion || {}).length} color="text-violet-600" />
      </div>

      <Card className="p-6 rounded-2xl border-slate-200 dark:border-slate-800">
        <h3 className="font-display text-xl mb-4">Lead Conversion Funnel</h3>
        <div className="space-y-2">
          {Object.entries(summary?.lead_conversion || {}).map(([k, v]) => (
            <div key={k} className="flex items-center gap-3">
              <div className="w-32 text-sm capitalize text-slate-600 dark:text-slate-300">{k.replace('_',' ')}</div>
              <div className="flex-1 bg-slate-100 dark:bg-slate-800 rounded-full h-6 relative overflow-hidden">
                <div className="absolute inset-y-0 left-0 bg-gradient-to-r from-amber-400 to-amber-600 rounded-full" style={{ width: `${Math.min(100, v * 5)}%` }} />
                <span className="absolute inset-0 flex items-center px-3 text-xs font-mono text-slate-800 dark:text-white">{v}</span>
              </div>
            </div>
          ))}
        </div>
      </Card>

      <Card className="p-6 rounded-2xl border-slate-200 dark:border-slate-800">
        <h3 className="font-display text-xl mb-4">Product Stock by Category</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {Object.entries(summary?.product_stock || {}).map(([k, v]) => (
            <div key={k} className="p-3 rounded-xl border border-slate-200 dark:border-slate-800">
              <div className="text-xs text-slate-500">{k}</div>
              <div className="font-display text-2xl">{v}</div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

const Metric = ({ icon: Icon, label, value, color }) => (
  <Card className="p-6 rounded-2xl border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900/60 card-lift">
    <Icon className={`w-6 h-6 ${color}`} strokeWidth={1.5} />
    <div className="mt-3 text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
    <div className="font-display text-3xl mt-1">{value}</div>
  </Card>
);
